import type { IncidentSummary, PageResult } from "@hydeck/mdt-contracts";
import {
  createEffect,
  createResource,
  createSignal,
  For,
  Show,
} from "solid-js";
import { DumpDetail, DumpField } from "../components/DumpDetail";
import { DumpIcon } from "../components/DumpIcon";
import { dumpFilterQuery, DumpList } from "../components/DumpList";
import { HistoryModal } from "../components/HistoryModal";
import { ShareReportSection } from "../components/ShareReportSection";
import { RecordRelations } from "../components/RecordRelations";
import { RichTextEditor } from "../components/RichTextEditor";
import { apiRequest } from "../core/api";
import { currentUser, hasPermission } from "../core/session";

export function IncidentsPage() {
  const [search, setSearch] = createSignal("");
  const [advanced, setAdvanced] = createSignal<Record<string, string>>({});
  const [revision, setRevision] = createSignal(0);
  const [selected, setSelected] = createSignal<IncidentSummary | null>(null);
  const [title, setTitle] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [bookmarked, setBookmarked] = createSignal(false);
  const [notice, setNotice] = createSignal("");
  const [historyOpen, setHistoryOpen] = createSignal(false);
  const [incidents] = createResource(
    () => [search(), JSON.stringify(advanced()), revision()] as const,
    ([query]) =>
      apiRequest<PageResult<IncidentSummary>>("/incidents", {
        token: currentUser()?.accessToken,
        query: { page: 1, limit: 50, search: query, ...dumpFilterQuery(advanced()) },
      }),
  );

  createEffect(() => {
    const incident = selected();
    setTitle(incident?.title ?? "");
    setDescription(incident?.description ?? "");
    setBookmarked(incident?.isBookmarked ?? false);
    setNotice("");
  });

  const createIncident = () => {
    const timestamp = new Date().toISOString();
    setSelected({
      id: 0,
      title: "New Incident",
      description: defaultIncidentTemplate(),
      createdAt: timestamp,
      updatedAt: timestamp,
      createdBy: currentUser()?.id ?? 0,
      createdByName: `${currentUser()?.firstName ?? ""} ${currentUser()?.lastName ?? ""}`.trim(),
      isLocked: false,
      tags: [],
      criminals: [],
      isBookmarked: false,
    });
  };

  const save = async () => {
    const incident = selected();
    if (!incident || !title().trim()) return;
    setSaving(true);
    try {
      const saved = await apiRequest<IncidentSummary>(
        incident.id ? `/incidents/${incident.id}` : "/incidents",
        {
          method: incident.id ? "PATCH" : "POST",
          token: currentUser()?.accessToken,
          body: {
            title: title(),
            // The dump persists the Quill/Yjs Delta as a JSON string.  Keep
            // older plain-text records readable while preventing accidental
            // double encoding on every autosave.
            description: normalizeDescription(description()),
          },
        },
      );
      setSelected(saved);
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const toggleLock = async () => {
    const incident = selected();
    if (!incident?.id) return;
    setSaving(true);
    try {
      const saved = await apiRequest<IncidentSummary>(`/incidents/${incident.id}`, {
        method: "PATCH",
        token: currentUser()?.accessToken,
        body: { isLocked: !incident.isLocked },
      });
      setSelected(saved);
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const toggleBookmark = async () => {
    const incident = selected();
    if (!incident?.id) return;
    setSaving(true);
    try {
      await apiRequest(
        `/bookmarks/incident/${incident.id}`,
        {
          method: bookmarked() ? "DELETE" : "POST",
          token: currentUser()?.accessToken,
        },
      );
      setBookmarked((value) => !value);
    } finally {
      setSaving(false);
    }
  };

  const deleteIncident = async () => {
    const incident = selected();
    if (!incident?.id || !window.confirm(`Delete incident #${incident.id}?`)) return;
    setSaving(true);
    try {
      await apiRequest(`/incidents/${incident.id}`, {
        method: "DELETE",
        token: currentUser()?.accessToken,
      });
      setSelected(null);
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  const refreshIncident = async () => {
    const incident = selected();
    if (!incident?.id) return;
    setSaving(true);
    try {
      setSelected(await apiRequest<IncidentSummary>(`/incidents/${incident.id}`, {
        token: currentUser()?.accessToken,
      }));
      setRevision((value) => value + 1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Show
      when={selected()}
      fallback={
        <DumpList
          label="Incidents"
          loading={incidents.loading}
          search={search()}
          onSearch={setSearch}
          onAdd={hasPermission("incidents.update") ? createIncident : undefined}
          advancedValues={advanced()}
          onAdvancedApply={setAdvanced}
          advancedFields={incidentAdvancedFields}
          entries={(incidents()?.items ?? []).map((incident) => ({
            id: incident.id,
            title: incident.title,
            description: incident.description,
            icon: "nav_legislation",
            leftLabel: `ID: ${incident.id}`,
            rightLabel: `${incident.createdByName} - ${formatTimestamp(incident.createdAt)}`,
            endLabel: (
              <ul class="ml-auto flex justify-end gap-1">
                <For each={incident.tags.slice(0, 4)}>
                  {(tag) => (
                    <li class="flex max-h-12 min-h-8 items-center justify-center bg-[rgba(255,255,255,.11)] px-2">
                      <p class="text-sm">{tag}</p>
                    </li>
                  )}
                </For>
              </ul>
            ),
            raw: incident,
          }))}
          onSelect={(entry) => setSelected(entry.raw as IncidentSummary)}
        />
      }
    >
      {(incident) => (
        <DumpDetail
          label={incident().id ? `View Incident #${incident().id}` : "Create Incident"}
          meta={
            <Show when={incident().id > 0}>
              <span class="truncate text-xs text-white-55">
                {incident().createdByName || "Unknown"} · {formatTimestamp(incident().createdAt)}
              </span>
            </Show>
          }
          onBack={() => setSelected(null)}
          actions={
            <>
              <Show when={incident().id > 0 && hasPermission("bookmarks.view")}>
                <button class="h-7 bg-blue-gradient px-2 text-xs" disabled={saving()} onClick={() => void toggleBookmark()}>
                  {bookmarked() ? "★ Saved" : "☆ Bookmark"}
                </button>
              </Show>
              <Show when={incident().id > 0}>
                <button class="h-7 bg-white-11 px-2 text-xs" disabled={saving()} onClick={() => void refreshIncident()}>
                  Refresh
                </button>
              </Show>
              <Show when={incident().id > 0 && hasPermission("incidents.view")}>
                <button class="h-7 bg-white-11 px-2 text-xs" disabled={saving()} onClick={() => setHistoryOpen(true)}>
                  History
                </button>
              </Show>
              <Show when={incident().id > 0 && hasPermission("incidents.update")}>
                <button class="h-7 bg-white-11 px-2 text-xs" disabled={saving()} onClick={() => void toggleLock()}>
                  {incident().isLocked ? "Unlock" : "Lock"}
                </button>
              </Show>
              <Show when={incident().id > 0 && hasPermission("incidents.delete")}>
                <button class="h-7 bg-red-gradient px-2 text-xs" disabled={saving()} onClick={() => void deleteIncident()}>
                  Delete
                </button>
              </Show>
              <Show when={hasPermission("incidents.update") && (!incident().isLocked || hasPermission("incidents.approve"))}>
              <button
                class="flex h-7 w-7 cursor-pointer items-center justify-center bg-green-gradient transition-all duration-200 ease-in-out hover:drop-shadow-green-gradient disabled:opacity-40"
                disabled={saving()}
                aria-label="Save incident"
                onClick={() => void save()}
              >
                <DumpIcon name="icon_save_docs" />
              </button>
              </Show>
            </>
          }
        >
          <div class="flex h-full min-h-0 flex-col gap-4 p-3 lg:flex-row">
            <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
              <ul class="flex min-h-11 w-full flex-col gap-3">
                <DumpField label="Incident ID" value={incident().id} disabled />
                <DumpField label="Title" value={title()} onInput={setTitle} />
                <DumpField
                  label="Created By"
                  value={incident().createdByName}
                  disabled
                />
              </ul>
              <div class="flex min-h-0 flex-1 flex-col gap-2 pl-4">
                <p>Description</p>
                <RichTextEditor
                  value={description()}
                  resetKey={incident().id}
                  roomName={incident().id > 0 ? `incident-${incident().id}` : undefined}
                  disabled={incident().isLocked || !hasPermission("incidents.update")}
                  onChange={setDescription}
                  onAutoSave={incident().id > 0 ? save : undefined}
                />
                <Show when={notice()}><p class="text-xs text-white-55">{notice()}</p></Show>
              </div>
            </div>
            <Show when={incident().id > 0}>
              <aside class="w-full max-w-full overflow-y-auto pr-1 lg:max-w-[30%]">
                <RecordRelations
                  type="incident"
                  item={incident()}
                  onReload={async () => {
                    const refreshed = await apiRequest<IncidentSummary>(
                      `/incidents/${incident().id}`,
                      { token: currentUser()?.accessToken },
                    );
                    setSelected(refreshed);
                  }}
                />
                <ShareReportSection
                  endpoint={`/incidents/${incident().id}/share`}
                  reportType="incidents"
                  label="Share incident"
                />
              </aside>
            </Show>
          </div>
        </DumpDetail>
      )}
    </Show>
      <HistoryModal
        kind="incident"
        id={selected()?.id ?? 0}
        open={historyOpen() && (selected()?.id ?? 0) > 0}
        canRevert={hasPermission("incidents.update")}
        onClose={() => setHistoryOpen(false)}
        onRevert={(value) => {
          setDescription(value);
          setNotice("History version loaded. Save the incident to apply it.");
          setHistoryOpen(false);
        }}
      />
    </>
  );
}

function formatTimestamp(value: string) {
  return new Date(value).toUTCString();
}

function defaultIncidentTemplate(): string {
  return JSON.stringify({
    ops: [
      { insert: "Incident Command (If Applicable):\n" },
      { insert: "First on Scene:\n" },
      { insert: "Location:\n" },
      { insert: "Statements:\n" },
    ],
  });
}

function normalizeDescription(value: string): string {
  if (!value) return JSON.stringify({ ops: [] });
  try {
    const parsed = JSON.parse(value);
    return JSON.stringify(parsed);
  } catch {
    return JSON.stringify({ ops: [{ insert: `${value}\n` }] });
  }
}

const incidentAdvancedFields = [
  { key: "createdBy", label: "Creator ID", type: "number" as const, placeholder: "User ID" },
  { key: "officers", label: "Officer IDs", placeholder: "Comma-separated IDs" },
  { key: "charges", label: "Charge ID", type: "number" as const, placeholder: "Charge ID" },
  { key: "tags", label: "Tag ID", type: "number" as const, placeholder: "Tag ID" },
  { key: "criminals", label: "Criminal IDs", placeholder: "Comma-separated IDs" },
  { key: "citizens", label: "Citizen IDs", placeholder: "Comma-separated IDs" },
  { key: "createdFrom", label: "Date From", type: "datetime-local" as const },
  { key: "createdTo", label: "Date To", type: "datetime-local" as const },
  {
    key: "sort",
    label: "Sort",
    type: "select" as const,
    options: [
      { value: "createdAt", label: "Created" },
      { value: "updatedAt", label: "Updated" },
      { value: "title", label: "Title" },
    ],
  },
  {
    key: "sortDirection",
    label: "Direction",
    type: "select" as const,
    options: [
      { value: "desc", label: "Newest first" },
      { value: "asc", label: "Oldest first" },
    ],
  },
];
